// admin/src/features/review/api.ts
//
// Lists AI-generated demo-photo candidates (`candidates/<exercise_id>/<ts>.png`
// in the public `exercise-media` bucket), joins them with the exercise
// they're for and whether it already has a demo photo, and applies the
// admin's approve/reject decision. Approve mirrors DemoPhotoEditor's save
// path exactly (admin/src/features/exercises/MediaEditors.tsx): compress
// with the same hero target, upload to a fresh exerciseMediaPath, replace
// (or insert) the demo_photo row, delete the old object, stamp
// exercises.updated_at. Reject just deletes the candidate -- the
// generation script treats "no demo photo and no candidate" as needing a
// new one, so nothing else needs to record the rejection.
import { supabase, MEDIA_BUCKET } from '../../lib/supabase';
import { compressImage, IMAGE_TARGETS } from '../../lib/image';
import { equipmentPhotoPath, exerciseMediaPath } from '../../lib/storagePaths';
import { deleteMedia, mediaUrl, uploadMedia } from '../../lib/storage';
import { insertMediaRow, updateMediaRow } from '../exercises/api';
import {
  chunk,
  folderNamesFromListing,
  pickLatestPerExercise,
  sortReviewCards,
  type CandidateFile,
  type ReviewCard,
} from './candidates';

const CANDIDATES_PREFIX = 'candidates';
/** Equipment photo candidates live one level deeper: candidates/equipment/<equipment_id>/<ts>.png. */
const EQUIPMENT_FOLDER = 'equipment';
/** Card ids for equipment candidates, so they never collide with an exercise uuid. */
const EQUIPMENT_KEY = 'equipment:';
export const isEquipmentCard = (card: { exerciseId: string }) => card.exerciseId.startsWith(EQUIPMENT_KEY);
/** Up to ~400 exercise folders per the data contract; listed in bounded-concurrency batches so one slow request doesn't serialize everything. */
const FOLDER_LIST_CONCURRENCY = 20;
const MAX_FOLDERS = 1000;

async function listFolder(path: string, limit: number) {
  const { data, error } = await supabase.storage.from(MEDIA_BUCKET).list(path, { limit, sortBy: { column: 'name', order: 'asc' } });
  if (error) throw error;
  return data ?? [];
}

/** Every candidate file across every exercise folder under `candidates/`. */
async function listCandidateFilesUnder(prefix: string, keyPrefix: string, skip: string[] = []): Promise<CandidateFile[]> {
  const topLevel = await listFolder(prefix, MAX_FOLDERS);
  const ids = folderNamesFromListing(topLevel).filter((id) => !skip.includes(id));

  const files: CandidateFile[] = [];
  for (const batch of chunk(ids, FOLDER_LIST_CONCURRENCY)) {
    const results = await Promise.all(
      batch.map(async (id) => {
        const entries = await listFolder(`${prefix}/${id}`, 100);
        return entries
          .filter((e) => e.id !== null)
          .map((e): CandidateFile => ({ exerciseId: keyPrefix + id, name: e.name, createdAt: e.created_at ?? null }));
      }),
    );
    for (const r of results) files.push(...r);
  }
  return files;
}

/** Every candidate file: exercise folders under `candidates/`, equipment folders under `candidates/equipment/`. */
async function listAllCandidateFiles(): Promise<CandidateFile[]> {
  const [exercise, equipment] = await Promise.all([
    listCandidateFilesUnder(CANDIDATES_PREFIX, '', [EQUIPMENT_FOLDER]),
    listCandidateFilesUnder(`${CANDIDATES_PREFIX}/${EQUIPMENT_FOLDER}`, EQUIPMENT_KEY),
  ]);
  return [...exercise, ...equipment];
}

function pathFor(key: string, name: string): string {
  return key.startsWith(EQUIPMENT_KEY)
    ? `${CANDIDATES_PREFIX}/${EQUIPMENT_FOLDER}/${key.slice(EQUIPMENT_KEY.length)}/${name}`
    : `${CANDIDATES_PREFIX}/${key}/${name}`;
}

export type ReviewData = {
  cards: ReviewCard[];
};

export async function loadReviewCards(): Promise<ReviewData> {
  const files = await listAllCandidateFiles();
  const latest = pickLatestPerExercise(files);
  if (latest.length === 0) return { cards: [] };

  const exerciseIds = latest.map((l) => l.exerciseId).filter((id) => !id.startsWith(EQUIPMENT_KEY));
  const equipmentIds = latest.map((l) => l.exerciseId).filter((id) => id.startsWith(EQUIPMENT_KEY)).map((id) => id.slice(EQUIPMENT_KEY.length));
  const [exercisesRes, mediaRes, equipmentRes] = await Promise.all([
    supabase.from('exercises').select('id, name_en, name_he, sport, muscle_group').in('id', exerciseIds),
    supabase.from('exercise_media').select('id, exercise_id, path, kind, rendition').eq('kind', 'demo_photo').in('exercise_id', exerciseIds),
    supabase.from('equipment').select('id, name_en, name_he, category, photo_path').in('id', equipmentIds),
  ]);
  if (exercisesRes.error) throw exercisesRes.error;
  if (mediaRes.error) throw mediaRes.error;
  if (equipmentRes.error) throw equipmentRes.error;

  const exerciseById = new Map((exercisesRes.data ?? []).map((e) => [e.id, e]));
  const demoByExercise = new Map((mediaRes.data ?? []).map((m) => [m.exercise_id, m as { id: string; path: string }]));
  // Equipment cards reuse the card shape: "sport" shows as Equipment, "muscle group" as its category.
  const equipmentByKey = new Map(
    (equipmentRes.data ?? []).map((q) => [
      EQUIPMENT_KEY + q.id,
      { info: { name_en: q.name_en, name_he: q.name_he, sport: 'equipment', muscle_group: q.category }, photo: q.photo_path as string | null },
    ]),
  );

  const cards: ReviewCard[] = latest.map(({ exerciseId, file }) => {
    const equipment = equipmentByKey.get(exerciseId);
    return {
      exerciseId,
      candidatePath: pathFor(exerciseId, file.name),
      exercise: equipment ? equipment.info : exerciseById.get(exerciseId) ?? null,
      existingDemoPath: equipment ? equipment.photo : demoByExercise.get(exerciseId)?.path ?? null,
      existingDemoId: equipment ? null : demoByExercise.get(exerciseId)?.id ?? null,
    };
  });

  return { cards: sortReviewCards(cards) };
}

/** Just the count, for the sidebar badge -- cheap enough to run on every navigation since it's the same calls `loadReviewCards` makes minus the exercise/media join. */
export async function countPendingCandidates(): Promise<number> {
  const files = await listAllCandidateFiles();
  return pickLatestPerExercise(files).length;
}

async function fetchCandidateBlob(path: string): Promise<Blob> {
  const url = mediaUrl(path);
  if (!url) throw new Error('Candidate has no public URL.');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not fetch the candidate image (${res.status}).`);
  return res.blob();
}

/**
 * Approves a candidate: fetch -> compress to the hero target (same as
 * DemoPhotoEditor) -> upload -> replace or insert the demo_photo row ->
 * delete the old demo object (if any) -> delete the candidate object.
 */
export async function approveCandidate(
  exerciseId: string,
  candidatePath: string,
  existingDemo: { id: string; path: string } | null,
): Promise<void> {
  const blob = await fetchCandidateBlob(candidatePath);
  const compressed = await compressImage(blob, IMAGE_TARGETS.hero);
  const newPath = exerciseMediaPath(exerciseId, compressed.ext);
  await uploadMedia(newPath, compressed.blob, compressed.mime);

  const meta = { mime: compressed.mime, bytes: compressed.blob.size, width: compressed.width, height: compressed.height };
  if (existingDemo) {
    await updateMediaRow(existingDemo.id, { path: newPath, ...meta });
    await deleteMedia([existingDemo.path]);
  } else {
    await insertMediaRow({ exercise_id: exerciseId, kind: 'demo_photo', rendition: 'phone', path: newPath, sort: 0, ...meta });
  }

  await deleteMedia([candidatePath]);

  // The exercises row's updated_at drives the app's cache invalidation (migration 0002's LWW
  // note); DemoPhotoEditor's save stays on the media row only, but this flow also moves an
  // exercise from "missing a photo" to "has one" so the app should see it refresh right away.
  const { error } = await supabase.from('exercises').update({ updated_at: new Date().toISOString() }).eq('id', exerciseId);
  if (error) throw error;
}

/**
 * Approves an equipment candidate the way EquipmentEditPage saves a photo:
 * compress to the equipment target, upload to a fresh equipmentPhotoPath,
 * point equipment.photo_path at it, delete the old photo and the candidate.
 */
async function approveEquipmentCandidate(equipmentId: string, candidatePath: string, oldPhotoPath: string | null): Promise<void> {
  const blob = await fetchCandidateBlob(candidatePath);
  const compressed = await compressImage(blob, IMAGE_TARGETS.equipment);
  const newPath = equipmentPhotoPath(equipmentId, compressed.ext);
  await uploadMedia(newPath, compressed.blob, compressed.mime);
  const { error } = await supabase
    .from('equipment')
    .update({ photo_path: newPath, updated_at: new Date().toISOString() })
    .eq('id', equipmentId);
  if (error) throw error;
  if (oldPhotoPath) await deleteMedia([oldPhotoPath]);
  await deleteMedia([candidatePath]);
}

/** Approves any card: an equipment photo or an exercise demo photo. */
export async function approveCard(card: ReviewCard): Promise<void> {
  if (isEquipmentCard(card)) {
    await approveEquipmentCandidate(card.exerciseId.slice(EQUIPMENT_KEY.length), card.candidatePath, card.existingDemoPath);
    return;
  }
  await approveCandidate(
    card.exerciseId,
    card.candidatePath,
    card.existingDemoId && card.existingDemoPath ? { id: card.existingDemoId, path: card.existingDemoPath } : null,
  );
}

/** Rejects a candidate: just delete the object. The generation script regenerates anything with no demo photo and no candidate. */
export async function rejectCandidate(candidatePath: string): Promise<void> {
  await deleteMedia([candidatePath]);
}
